import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'
import isEmpty from 'lodash-es/isEmpty'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { SIZE } from '../../../../constants/size'
import { useAsyncCallback } from '../../../../hooks'
import { parseServerValidation } from '../../../../utils/validation'
import Button, { BUTTON_HEIGHT_24 } from '../../../Button'
import { ConfirmationModal } from '../../../Confirmation'
import {
  FormBody,
  FormFieldsContainer,
  FormInlineNotifications,
  useFormValidationErrorsNotification,
} from '../../../Form'
import { Content } from '../../../Modal'
import { deleteLabel } from '../../Label.api'
import { canDeleteLabel } from '../../Label.utils'
import { QA_NAME_LABEL_DELETE_BTN } from '../LabelForm.constants'

import { ColorField } from './ColorField'
import { EntityField } from './EntityField'
import { PrivacyField } from './PrivacyField'
import { TitleField } from './TitleField'

import * as styles from './FormFields.module.css'

import DeleteIcon from '../../../../icons/delete.svg'

function FormFields({
  ContentComponent = Content,
  contentComponentProps: {
    size: contentSize = SIZE.X_LARGE,
    ...contentComponentProps
  } = EMPTY_OBJECT,
  initValues,
  isEdit,
  notifications,
  setModal,
  onCancel,
  onCloseModal,
  onDeleteSuccess,
  onRemoveNotification,
  onSetNotification,
}) {
  const { t } = useTranslation()

  const { dirty, isSubmitting, setErrors } = useFormikContext()

  useFormValidationErrorsNotification({
    onRemoveNotification,
    onSetNotification,
  })

  function handleCancel() {
    onCancel({ dirty })
  }

  const canDelete = isEdit && canDeleteLabel(initValues)

  const handleDelete = useAsyncCallback(() => {
    // `initValues` is undefined in create mode; an unguarded `.id` here makes
    // the compiler hoist `initValues.id` into a render-time dep check, which
    // crashes the create-label modal before the callback is ever invoked.
    return deleteLabel(initValues?.id)
      .then(() => {
        onDeleteSuccess?.()
      })
      .catch(error => {
        onCloseModal()

        const errors = error?.response?.data ?? EMPTY_OBJECT

        if (!isEmpty(errors)) {
          setErrors(parseServerValidation(errors))
        }
      })
  }, [initValues?.id, onCloseModal, onDeleteSuccess, setErrors])

  function handleOpenDeleteConfirm() {
    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: t('Modal.DeleteBtnKey'),
        }}
        description={t('Modal.DeleteConfirmationTitleKey')}
        title={t('Modal.DeleteModalTitlekey')}
        onCancel={onCloseModal}
        onSubmit={handleDelete}
      />
    )
  }

  return (
    <FormBody
      actionsProps={{
        ButtonAdd: false,
        ButtonClear: false,
        UploadComponent: false,
        cancelProps: {
          onClick: handleCancel,
        },
        children: canDelete ? (
          <Button.Icon
            isGrey
            Icon={DeleteIcon}
            className={styles.deleteButton}
            disabled={isSubmitting}
            height={BUTTON_HEIGHT_24}
            qaName={QA_NAME_LABEL_DELETE_BTN}
            tooltip={t('Modal.DeleteBtnKey')}
            onClick={handleOpenDeleteConfirm}
          />
        ) : null,
        className: styles.actions,
        isSubmitting,
        submitProps: {
          isLoading: isSubmitting,
          title: isEdit ? 'Modal.SaveBtnKey' : 'Modal.CreateBtnKey',
        },
      }}
    >
      <ContentComponent {...contentComponentProps} size={contentSize}>
        <FormInlineNotifications notifications={notifications} />
        <FormFieldsContainer className={styles.fieldsContainer}>
          <TitleField />
          <ColorField />
          <EntityField />
          <PrivacyField />
        </FormFieldsContainer>
      </ContentComponent>
    </FormBody>
  )
}

export default FormFields
